import React, { useEffect, useMemo, useState } from 'react'
import { Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap'
import { ExternalLink, Filter, Search } from 'lucide-react'
import { useInvoiceHistory, useSubscriptionDevices } from '@data/subscriptions/subscriptions'
import { IInvoiceRow } from '@data/subscriptions/model/subscriptions.model'
import { useAuthStore } from '@store/auth.store'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { InvoiceQuickFilter } from './SubscriptionAlerts'
import FinalizeInvoiceModal from './FinalizeInvoiceModal'

const QUICK_FILTERS: { key: InvoiceQuickFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'paid', label: 'Paid' },
  { key: 'open', label: 'Open' },
  { key: 'past_due', label: 'Past Due' },
  { key: 'draft', label: 'Draft' },
]

const STATUS_BADGE_CLASS: Record<string, string> = {
  paid: 'sf-status-badge sf-status-badge--healthy',
  open: 'sf-status-badge sf-status-badge--upcoming',
  past_due: 'sf-status-badge sf-status-badge--overdue',
  uncollectible: 'sf-status-badge sf-status-badge--restricted',
  void: 'sf-status-badge sf-status-badge--none',
  draft: 'sf-status-badge sf-status-badge--none',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function matchesQuickFilter(inv: IInvoiceRow, filter: InvoiceQuickFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'past_due') {
    // Past Due = open invoice past its period_end (best approximation given current data shape).
    if (inv.status !== 'open') return false
    if (!inv.periodEnd) return false
    return new Date(inv.periodEnd).getTime() < Date.now()
  }
  return inv.status === filter
}

interface Props {
  accountId: number
  initialFilter?: InvoiceQuickFilter
}

const InvoiceHistory: React.FC<Props> = ({ accountId, initialFilter = 'all' }) => {
  const [locationName, setLocationName] = useState<string | 'all'>('all')
  const [deviceName, setDeviceName] = useState<string | 'all'>('all')
  const [invoiceQuery, setInvoiceQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [quickFilter, setQuickFilter] = useState<InvoiceQuickFilter>(initialFilter)
  const [finalizeTarget, setFinalizeTarget] = useState<IInvoiceRow | null>(null)

  const { data: invoices, isLoading, isError } = useInvoiceHistory(accountId)
  const { data: devices } = useSubscriptionDevices(accountId)

  const permissions = useAuthStore((state) => state.permissions)
  const canFinalize = hasPermissions(permissions, ['FINALIZE:INVOICE'])

  // Re-apply the quick filter when the parent navigates here with a new initialFilter.
  useEffect(() => {
    setQuickFilter(initialFilter)
  }, [initialFilter])

  const locationOptions = useMemo(() => {
    const set = new Set<string>()
    for (const inv of invoices ?? []) if (inv.locationName) set.add(inv.locationName)
    for (const d of devices ?? []) set.add(d.locationName)
    return Array.from(set).sort()
  }, [invoices, devices])

  const deviceOptions = useMemo(() => {
    const set = new Set<string>()
    for (const inv of invoices ?? []) if (inv.deviceName) set.add(inv.deviceName)
    for (const d of devices ?? []) if (d.deviceName) set.add(d.deviceName)
    return Array.from(set).sort()
  }, [invoices, devices])

  const filtered = useMemo(() => {
    const q = invoiceQuery.trim().toLowerCase()
    return (invoices ?? []).filter((inv) => {
      if (locationName !== 'all' && inv.locationName !== locationName) return false
      if (deviceName !== 'all' && inv.deviceName !== deviceName) return false
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false
      if (!matchesQuickFilter(inv, quickFilter)) return false
      if (q && !(inv.number ?? inv.invoiceId).toLowerCase().includes(q)) return false
      return true
    })
  }, [invoices, locationName, deviceName, statusFilter, quickFilter, invoiceQuery])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" />
      </div>
    )
  }

  if (isError) {
    return <p className="text-danger">Failed to load invoice data.</p>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Invoice History</h4>
        <span className="sf-synced-pill">Synced with Stripe</span>
      </div>

      <Card className="sf-filter-card mb-3">
        <Card.Body>
          <div className="sf-filter-card__title">FILTER HISTORY</div>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Label className="text-muted small fw-semibold">LOCATION</Form.Label>
              <InputGroup>
                <InputGroup.Text><Filter size={14} /></InputGroup.Text>
                <Form.Select
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                >
                  <option value="all">All accessible Locations</option>
                  {locationOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </div>
            <div className="col-md-3">
              <Form.Label className="text-muted small fw-semibold">DEVICE</Form.Label>
              <InputGroup>
                <InputGroup.Text><Search size={14} /></InputGroup.Text>
                <Form.Select
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                >
                  <option value="all">All Devices</option>
                  {deviceOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </div>
            <div className="col-md-3">
              <Form.Label className="text-muted small fw-semibold">SEARCH INVOICE #</Form.Label>
              <InputGroup>
                <InputGroup.Text><Search size={14} /></InputGroup.Text>
                <Form.Control
                  placeholder="INV-..."
                  value={invoiceQuery}
                  onChange={(e) => setInvoiceQuery(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="col-md-3">
              <Form.Label className="text-muted small fw-semibold">STATUS</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="open">Open</option>
                <option value="void">Void</option>
                <option value="uncollectible">Uncollectible</option>
                <option value="draft">Draft</option>
              </Form.Select>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3">
            <span className="text-muted small fw-semibold me-2">QUICK FILTERS:</span>
            {QUICK_FILTERS.map((qf) => (
              <span
                key={qf.key}
                className={`sf-status-pill ${quickFilter === qf.key ? 'active' : ''}`}
                onClick={() => setQuickFilter(qf.key)}
              >
                {qf.label}
              </span>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Created</th>
              <th>Device</th>
              <th>Location</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  No invoices found
                </td>
              </tr>
            ) : (
              filtered.map((inv) => {
                const created = inv.periodStart ?? null
                const url = inv.hostedInvoiceUrl ?? inv.invoicePdf
                return (
                  <tr key={inv.invoiceId}>
                    <td>{inv.number ?? inv.invoiceId.substring(0, 12)}</td>
                    <td>{formatDate(created)}</td>
                    <td className="fw-semibold">{inv.deviceName ?? '—'}</td>
                    <td>{inv.locationName ?? '—'}</td>
                    <td>
                      <span className={STATUS_BADGE_CLASS[inv.status] ?? 'sf-status-badge sf-status-badge--none'}>
                        {inv.status}
                      </span>
                    </td>
                    <td>
                      {canFinalize && inv.status === 'draft' && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 me-3"
                          onClick={() => setFinalizeTarget(inv)}
                        >
                          {inv.requiresPo ? 'Add PO to invoice' : 'Finalize invoice'}
                        </button>
                      )}
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="btn btn-link btn-sm p-0">
                          View Invoice <ExternalLink size={12} className="ms-1" />
                        </a>
                      ) : (
                        !(canFinalize && inv.status === 'draft') && (
                          <span className="text-muted small">—</span>
                        )
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </Card>

      {finalizeTarget !== null && (
        <FinalizeInvoiceModal
          // Remount per invoice so the PO input resets between rows.
          key={finalizeTarget.invoiceId}
          invoice={finalizeTarget}
          accountId={accountId}
          show={finalizeTarget !== null}
          onHide={() => setFinalizeTarget(null)}
        />
      )}
    </div>
  )
}

export default InvoiceHistory
